/*
 * Orders — V1.9.1 (design handoff README §Screens → 2. Orders; App
 * Shell.dc.html is the measured reference; QA-CHECKLIST.md carries the
 * measured assertions). Presentation rebuilt to the measured design; DATA
 * LOGIC PRESERVED: box-office orders (DataProvider) + my-api allocation
 * holders merged into seats[] (useOrderSeats — the my-api holder is
 * allocation-authoritative), honest notices while allocation is unreadable,
 * re-auth on 401 (AllocNotice signed-out), POST-EVENT CLOSE gating.
 *
 * STRUCTURE — three layers:
 *  1. Order card: slate boundary, clickable collapsing header (caret · mono
 *     ORDER eyebrow · ref in the order's STATUS hue · {event} · {date} ·
 *     total + Invoice ›), summary line + 5px progress, mono label TICKETS IN
 *     THIS ORDER.
 *  2. ONE BRACKET PER TICKET, coloured by ticket TYPE — type colours assigned
 *     once per LIST in order of first appearance (ordersView.typeColourMap);
 *     two Doubles tickets in one order are two blocks in the SAME colour.
 *  3. Place rows — one per person, ONE grid for every row state, every cell
 *     explicitly placed (QA §3).
 *
 * COLOUR (QA §2, hard rule): teal/amber = state; blue→magenta arc = ticket
 * type; slate = container boundary. NO green/amber/teal/red for identity.
 *
 * CLIENT-STATE OVERLAY (// TODO WIRE, assignStore.ts): child / guardian /
 * teen seat records the allocation API cannot express are merged over the
 * my-api seat state for display — my-api stays authoritative for the states
 * it CAN express.
 */
import { useCallback, useEffect, useState, useSyncExternalStore, type CSSProperties } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useShellChrome } from '../components/Shell'
import { startSignIn } from '../auth/startSignIn'
import { LiveDataNotice, useData } from '../data/DataProvider'
import { browseEventsUrl, invoiceUrlForOrder, isPastEvent } from '../data/pastEvents'
import type { Money, Order, Seat, Ticket } from '../data/types'
import { useLocale, type TranslateFn } from '../i18n/useLocale'
import { orderSummary, seatUnassignable } from './orders/ordersModel'
import {
  groupTicketsByUnit, seatKeyFor, tagColWidth, teamAccent, teamUnitOrdinals, typeColourMap, type TypeColour,
} from './orders/ordersView'
import {
  getAssignStoreSnapshot, localAssignmentDisplayName, stampLocalAssignmentTicketId,
  subscribeAssignStore, type LocalAssignment,
} from './orders/assignStore'
import { useOrderSeats, type AllocState } from './orders/useOrderSeats'
import { useGetReadyLiveOptional } from './GetReady'
import { formatEventDate } from './HeatSelect'
import { RemovedHoldingsNote } from '../components/RemovedHoldingsNote'

// ---------------------------------------------------------------------------
// EVENT-tier date (dispatch BOARDROOM-2026-09-06-145435)
// ---------------------------------------------------------------------------

/** The white EVENT-tier date for an order card. Prefers the my-api
 *  authoritative event start (formatEventDate — the app's canonical date
 *  token, shared with Tickets/HeatSelect); falls back to the box-office
 *  event's own display `when`, taking the DATE portion before the ` · ` time
 *  (so a date still renders while allocations are loading/signed-out). Returns
 *  '' when neither source knows a date — the card then shows NOTHING rather
 *  than a placeholder (directive: "no date = show nothing").
 *
 *  ONE order = ONE event (Order.eventId) = ONE date: the directive's multi-day
 *  RANGE case cannot arise on the current wire (no per-ticket date field), so
 *  the single-date path is the only reachable one. Kept a pure single-value
 *  helper; a range would need a per-ticket/per-heat date contract from infra. */
export function orderEventDate(eventStartsAt?: string | null, when?: string): string {
  const token = eventStartsAt ? formatEventDate(eventStartsAt) : ''
  if (token) return token
  return (when ?? '').split('·')[0]?.trim() ?? ''
}

// trim(): a zero-total promo order with no currency signal on the wire
// (Currency '') renders a bare '0', not '0 ' (BOARDROOM-2026-09-03-053306).
const money = (m: Money) => `${m.amount.toLocaleString()} ${m.currency}`.trim()

// ---------------------------------------------------------------------------
// Client-record overlay (TODO WIRE seam) — effective seat states
// ---------------------------------------------------------------------------

/** Merge local child/guardian/teen records over the my-api seat state. my-api
 *  is authoritative for allocation STATE where it CAN express it; a local
 *  record lifts a seat my-api reports unassigned (child/guestchild/teen have
 *  no server contract) and — since the child-identity ruling
 *  (BOARDROOM-2026-09-02-150714 item 2) — supplies the seat's DISPLAY NAME
 *  even when the server DOES hold the seat: a child ticket whose server
 *  holder is invitee/claimed (farmed to a parent email, claimed pre-slice)
 *  carries no child identity on the wire, so the row label showed the
 *  guardian's email/name — the ruling puts the CHILD's name first on every
 *  surface. Server status/email are never touched; only the name overlays. */
/** STALE-RECORD GUARD (PR#124 review P1-2): does this local record still
 *  describe THIS seat's wire state? A record is STALE — and must not overlay
 *  a name, lift a status or drive a sub-line — when:
 *   - both ticket row ids are known and DIFFER (the seat re-materialised on a
 *     different row than the one the record was stamped for), or
 *   - it is a guestparent record whose recorded parent email no longer
 *     matches the seat's LIVE holder email (the seat was re-invited to a
 *     DIFFERENT adult — "Waiting for {old parent}" would be a lie).
 *  Exported for the revoke-then-reassign pins. */
export function recordMatchesSeat(rec: LocalAssignment, s: Seat): boolean {
  if (rec.ticketId && s.ticketId && rec.ticketId !== s.ticketId) return false
  if (rec.mode === 'guestparent' && s.status !== 'unassigned') {
    const seatEmail = (s.holder?.email ?? '').trim().toLowerCase()
    const recEmail = rec.parentEmail.trim().toLowerCase()
    if (seatEmail && recEmail && seatEmail !== recEmail) return false
  }
  return true
}

export function applyLocalRecords(
  orderRef: string,
  tickets: Ticket[],
  records: Record<string, LocalAssignment>,
): { tickets: Ticket[]; recordFor: (ti: number, si: number) => LocalAssignment | null } {
  // recordFor yields ONLY records that still match the seat's wire state
  // (review P1-2): a stale record neither overlays a name nor drives a
  // sub-line. Matched against the ORIGINAL wire seat — the overlay itself
  // only applies to matching records, so the two views always agree.
  const recordFor = (ti: number, si: number): LocalAssignment | null => {
    const raw = records[seatKeyFor(orderRef, ti, si)] ?? null
    if (!raw) return null
    const s = tickets[ti]?.seats[si]
    return s && recordMatchesSeat(raw, s) ? raw : null
  }
  const merged = tickets.map((t, ti) => ({
    ...t,
    seats: t.seats.map((s, si) => {
      const rec = recordFor(ti, si)
      if (!rec) return s
      if (s.status !== 'unassigned') {
        // Server-held seat: state stays server truth; the locally-recorded
        // subject's NAME leads the row (child-identity ruling — the child's
        // name for every mode incl. guestparent, whose seat is the child's
        // even though the holder is the parent). An unnamed record changes
        // nothing — never a fabricated name, and the honest email label
        // remains (the guardian holds it and no child name is known here).
        const childName = localAssignmentDisplayName(rec)
        return childName ? { ...s, holder: { ...s.holder, name: childName } } : s
      }
      return rec.mode === 'guestparent'
        ? { ...s, status: 'pending' as const, holder: { name: rec.name || undefined, email: rec.parentEmail || undefined } }
        : { ...s, status: 'assigned' as const, holder: { name: localAssignmentDisplayName(rec) || undefined } }
    }),
  }))
  return { tickets: merged, recordFor }
}

/** Local-record sub-line for a seat row (client-state modes). `seatStatus`
 *  (the SERVER's allocation state) picks the honest guestparent phase copy:
 *  pending → invited/waiting; assigned (claimed+) → accepted/in-their-hands
 *  (parent-signing visibility, ruling item 5 — Orders row + Get-ready card
 *  both reflect the state). */
function recordSub(rec: LocalAssignment, t: TranslateFn, seatStatus?: Seat['status']): string {
  switch (rec.mode) {
    case 'guestparent': {
      if (seatStatus === 'assigned') {
        const parent = rec.parentName || rec.parentEmail
        return parent
          ? t('orders.row.subGuestparentClaimed', { parent })
          : t('orders.row.subGuestparentClaimedNoName')
      }
      return rec.name
        ? t('orders.row.subGuestparent', { child: rec.name })
        : t('orders.row.subGuestparentNoName')
    }
    case 'teen':
      return rec.teenLogin && rec.teenEmail
        ? t('orders.row.subTeenLogin', { email: rec.teenEmail })
        : t('orders.row.subTeen')
    case 'guestchild':
      return t('orders.row.subGuestchild')
    default:
      return t('orders.row.subChild')
  }
}

// ---------------------------------------------------------------------------
// Order card (presentational — tests render it with the seeded fixtures)
// ---------------------------------------------------------------------------

export interface OrderCardData {
  ref: string
  event: string
  /** EVENT-tier date — the event's OWN date (orderEventDate: my-api
   *  eventStartsAt via formatEventDate, else the box-office `when` date-part).
   *  Rendered WHITE at the event name's size so name + date read as one event
   *  line. NOT the order purchase date. Absent ⇒ nothing renders. */
  eventDate?: string
  /** Order purchase date (Order.purchasedAt, e.g. "5 Sept 2026") — belongs to
   *  the ORDER tier (with the ref), never presented as the event date. */
  date: string
  total: string
  tickets: Ticket[]
  /** Real invoice artefact from box-office. When absent the link hides —
   *  never a dead placeholder. */
  invoiceUrl?: string
}

/** Small teal "Invoice ›" link beside the total. Renders ONLY when a real
 *  invoiceUrl exists; the chevron encodes direction (swapped in code). */
function InvoiceLink({ href, chev, label }: { href?: string; chev: string; label: string }) {
  if (!href) return null
  return (
    <a
      data-testid="orders-invoice"
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      style={{ display: 'inline-block', fontFamily: 'inherit', fontSize: 12, fontWeight: 700, color: 'var(--tru-status-teal-bright)', textDecoration: 'none', cursor: 'pointer' }}
    >{`${label} ${chev}`}</a>
  )
}

export function OrderCard({
  data, expanded, onToggle, onAssignSeat, onEditSeat, eventClosed = false,
  readOnly = false, typeColours, tagCol,
}: {
  data: OrderCardData
  expanded: boolean
  onToggle?: () => void
  /** Open the assign flow for an UNASSIGNED place (Assign now). */
  onAssignSeat?: (ticketId: string, seatNo: number) => void
  /** Open the assign flow for a FILLED place (✎ — reassign). */
  onEditSeat?: (ticketId: string, seatNo: number) => void
  /** POST-EVENT CLOSE (BOARDROOM-2026-08-25-044751): server-computed flag —
   *  assign/edit affordances drop with honest copy. */
  eventClosed?: boolean
  /** PAST-EVENT read-only render (BOARDROOM-2026-09-01-175306 item 2): the
   *  order's event settled past the grace boundary — tickets/details stay
   *  viewable but the card is STRICTLY inert: no Assign/✎ CTAs (implies the
   *  eventClosed gating) AND no amber needs-action states anywhere (summary
   *  pill, ref hue, block label, place label) — a finished event must never
   *  nag for action. */
  readOnly?: boolean
  /** Type → colour map decided once for the WHOLE list (typeColourMap). A
   *  card rendered alone derives its own (tests/fixtures). */
  typeColours?: Map<string, TypeColour>
  /** 56/0 seat-tag gutter decided once for the WHOLE list (tagColWidth). */
  tagCol?: 56 | 0
}) {
  const { isMobile } = useShellChrome()
  const { t, dir } = useLocale()
  // Client-state overlay (TODO WIRE seam) — reactive so a completed assign
  // flow reflects immediately.
  const store = useSyncExternalStore(subscribeAssignStore, getAssignStoreSnapshot, getAssignStoreSnapshot)
  // TEAM-ADJACENT order (BOARDROOM-2026-09-09-182845): sorted BEFORE
  // applyLocalRecords so the index-keyed seat records and every downstream
  // derivation (colours, ordinals, backfill) see one consistent order.
  const { tickets, recordFor } = applyLocalRecords(data.ref, groupTicketsByUnit(data.tickets), store.assigned)

  // BACKFILL + REVALIDATION (child-identity slice; review P2-2): a record
  // gets the seat's CURRENT wire ticketId stamped the moment this card has
  // both in hand — including OVERWRITING a frozen-wrong stamp (the seat map
  // is re-derived fresh every render; the stamp is never trusted over it).
  // Reads the RAW store record (not recordFor, which hides stale records) so
  // a mismatched stamp is exactly what gets corrected. The stamp is a SILENT
  // store write (review P1-1) — no notify loop, converges immediately.
  useEffect(() => {
    tickets.forEach((tk, ti) => tk.seats.forEach((s, si) => {
      const raw = store.assigned[seatKeyFor(data.ref, ti, si)]
      if (raw && s.ticketId && raw.ticketId !== s.ticketId) {
        stampLocalAssignmentTicketId(raw.seatKey, s.ticketId)
      }
    }))
  })

  const colours = typeColours ?? typeColourMap([{ tickets }])
  const tag = tagCol ?? tagColWidth([{ tickets }])
  // Per-team accent ordinals (BOARDROOM-2026-09-09-161918): distinct team
  // purchases in this card, block order — feeds teamAccent so two teams of
  // the SAME type stop rendering identical blocks.
  const unitOrdinals = teamUnitOrdinals(tickets)
  const s = orderSummary(tickets)
  // A read-only past card never wears the amber needs-action state.
  const need = s.needTickets > 0 && !readOnly
  // ORDER-tier accent (directive BOARDROOM-2026-09-06-145435): the WHOLE order
  // line (eyebrow · ref · date) wears ONE hue at ONE size — amber while a place
  // needs action, else the resting green — so it reads as a single 'order info'
  // unit distinct from the white event line above.
  const orderAccent = need ? 'var(--tru-status-amber)' : 'var(--tru-status-done-text)'
  // Inert = no assign/edit affordances (past implies the eventClosed gating).
  const inert = eventClosed || readOnly
  const rowChev = dir === 'rtl' ? '‹' : '›'

  // --- row grid (ONE grid for every row state; every cell explicitly placed,
  // QA §3). Desktop track 3 is a FIXED width decided per list — never
  // max-content (it collapses to 0 without a tag and shifts the text origin).
  const gridCols = isMobile ? '40px 8px minmax(0,1fr)' : `36px 8px ${tag}px minmax(0,1fr) auto`
  const rowStyle: CSSProperties = {
    display: 'grid',
    gridTemplateColumns: gridCols,
    columnGap: isMobile ? 8 : 10,
    ...(isMobile ? { rowGap: 6 } : {}),
    alignItems: 'center',
    padding: isMobile ? '8px 10px' : '7px 12px',
    borderBlockStart: '1px solid var(--tru-hairline-05)',
  }

  return (
    <div
      id={`orders.card.${data.ref}`}
      data-testid="orders-card"
      data-order-ref={data.ref}
      style={{
        background: 'var(--tru-surface-2)',
        border: 'var(--tru-slate-border)',
        borderRadius: 'var(--tru-slate-radius)',
        overflow: 'hidden',
        marginBlockEnd: 'var(--tru-card-gap)',
        boxShadow: 'var(--tru-slate-halo), var(--tru-slate-shadow)',
      }}
    >
      {/* header — clickable, collapses the card; slate tint fill + slate rule */}
      <div
        onClick={onToggle}
        style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '14px 16px', background: 'var(--tru-slate-header-fill)', borderBlockEnd: 'var(--tru-slate-header-rule)', cursor: onToggle ? 'pointer' : undefined }}
      >
        <button
          data-testid="orders-card-toggle"
          aria-expanded={expanded}
          onClick={(e) => { e.stopPropagation(); onToggle?.() }}
          style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flex: 1, minWidth: 0, border: 0, background: 'transparent', fontFamily: 'inherit', textAlign: 'start', padding: 0, cursor: 'pointer', color: 'inherit' }}
        >
          <span aria-hidden style={{ flex: 'none', color: 'var(--tru-text-tertiary)', fontSize: 12, marginBlockStart: 2 }}>{expanded ? '▾' : '▸'}</span>
          <span style={{ flex: 1, minWidth: 0 }}>
            {/* EVENT tier (white) — name + the event's OWN date, SAME colour +
                SAME size so name and date read as one event line. */}
            <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: 'var(--tru-text-primary)', letterSpacing: '-0.01em', lineHeight: 1.25, overflowWrap: 'anywhere' }}>{data.event}</span>
            {data.eventDate && (
              <span data-testid="orders-event-date" style={{ display: 'block', fontSize: 15, fontWeight: 600, color: 'var(--tru-text-primary)', letterSpacing: '-0.01em', lineHeight: 1.25, marginBlockStart: 2, overflowWrap: 'anywhere' }}>{data.eventDate}</span>
            )}
            {/* ORDER tier — ONE accent hue, UNIFORM size across the whole line
                (eyebrow · order id · purchase date) so it reads as one 'order
                info' unit distinct from the white event line above. */}
            <span data-testid="orders-line" className="mono" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBlockStart: 8, fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', color: orderAccent }}>
              <span style={{ letterSpacing: '.14em' }}>{t('orders.card.eyebrow')}</span>
              <span data-testid="orders-ref" style={{ letterSpacing: '.05em' }}>{data.ref}</span>
              <span aria-hidden>·</span>
              <span>{data.date}</span>
            </span>
          </span>
        </button>
        <div style={{ textAlign: 'end', flex: 'none' }}>
          <div data-testid="orders-total" style={{ fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap' }}>{data.total}</div>
          <InvoiceLink href={data.invoiceUrl} chev={rowChev} label={t('orders.card.invoice')} />
        </div>
      </div>

      {/* summary line + 5px progress */}
      <div style={{ padding: '10px 16px' }}>
        <div data-testid="orders-summary" style={{ display: 'flex', alignItems: 'center', gap: 9, marginBlockEnd: 7, whiteSpace: 'nowrap', overflow: 'hidden' }}>
          <span style={{ fontSize: 12.5, fontWeight: 800, color: 'var(--tru-text-primary)' }}>
            {s.ticketCount === 1 ? t('orders.summary.tickets.one') : t('orders.summary.tickets.other', { n: s.ticketCount })}
          </span>
          {/* needs-action pill — suppressed outright on a read-only past card
              (a finished event never nags; BOARDROOM-2026-09-01-175306). */}
          {need && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 700, color: 'var(--tru-status-amber)' }}>
              <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--tru-status-amber)' }} />
              {isMobile
                ? t('orders.summary.need.mobile', { n: s.needTickets })
                : s.needTickets === 1 ? t('orders.summary.need.one') : t('orders.summary.need.other', { n: s.needTickets })}
            </span>
          )}
          {s.doneTickets > 0 && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11.5, fontWeight: 700, color: 'var(--tru-status-done-text)' }}>
              <span aria-hidden style={{ width: 7, height: 7, borderRadius: '50%', background: 'var(--tru-status-teal)' }} />
              {isMobile ? t('orders.summary.done.mobile', { n: s.doneTickets }) : t('orders.summary.done.desktop', { n: s.doneTickets })}
            </span>
          )}
          <span style={{ flex: 1 }} />
          {/* `{d} of {t} people assigned` — desktop only */}
          {!isMobile && (
            <span style={{ fontSize: 10.5, color: 'var(--tru-text-tertiary)' }}>
              {s.total === 1
                ? t('orders.summary.assigned.one', { done: s.assigned, total: s.total })
                : t('orders.summary.assigned.other', { done: s.assigned, total: s.total })}
            </span>
          )}
        </div>
        <div style={{ height: 'var(--tru-progress-h)', borderRadius: 'var(--tru-progress-radius)', background: 'var(--tru-progress-track)', overflow: 'hidden' }}>
          <div data-testid="orders-progress" style={{ height: '100%', borderRadius: 'var(--tru-progress-radius)', background: 'var(--tru-progress-fill)', width: `${s.pct}%` }} />
        </div>
      </div>

      {/* ticket blocks — one BRACKET per TICKET, coloured by TYPE */}
      {expanded && (
        <div style={{ background: 'var(--tru-surface-1)', borderBlockStart: '1px solid var(--tru-hairline-07)' }}>
          <div className="mono" style={{ fontSize: 9.5, letterSpacing: '.1em', color: 'var(--tru-text-muted)', padding: '12px 16px 2px' }}>
            {t('orders.card.ticketsLabel')}
          </div>
          <div style={{ padding: '8px 14px 12px' }}>
            {tickets.map((tk, ti) => {
              const base = colours.get(tk.typeName || 'Ticket') ?? { c: 'var(--tru-ticket-1)', tint: 'var(--tru-ticket-1-tint)' }
              // Team blocks wear the per-UNIT accent (BOARDROOM-2026-09-09-
              // 161918) — first team keeps the type colour, siblings of the
              // SAME unit share theirs, adjacent units differ. Non-team
              // blocks (solos / fallback / degraded wire) keep type colour.
              const P = tk.teamUnitId ? teamAccent(base, unitOrdinals.get(tk.teamUnitId) ?? 0) : base
              // Member label "… - Ticket {n}" ONLY on per-row-wire team
              // members (Duncan naming ruling: "Ticket", matching the
              // "{n} tickets" header — never "Person").
              const blockLabel = tk.teamMemberSeq
                ? t('orders.block.unitTicket', { type: tk.typeName, n: tk.teamMemberSeq })
                : tk.typeName
              const multi = tk.seats.length > 1
              const needN = tk.seats.filter((x) => x.status === 'unassigned').length
              const anyPending = tk.seats.some((x) => x.status === 'pending')
              return (
                <div
                  key={`${tk.id}.${ti}`}
                  className="bracket"
                  data-testid="orders-ticket-block"
                  data-type={tk.typeName}
                  data-colour={P.c}
                  data-team-unit={tk.teamUnitId}
                  style={{ '--bracket-color': P.c, overflow: 'hidden', marginBlockEnd: 8 } as CSSProperties}
                >
                  {/* block head — swatch · type · mono count · state label */}
                  <div
                    data-testid="orders-block-head"
                    style={{ display: 'flex', alignItems: 'center', gap: 8, padding: `7px ${isMobile ? 10 : 12}px`, background: P.tint }}
                  >
                    <span aria-hidden style={{ width: 8, height: 8, flex: 'none', borderRadius: 2, background: P.c }} />
                    <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 800, letterSpacing: '-0.01em', overflowWrap: 'anywhere' }}>{blockLabel}</span>
                    <span data-testid="orders-block-count" className="mono" style={{ flex: 'none', fontSize: 9, letterSpacing: '.08em', color: 'var(--tru-text-muted)' }}>
                      {multi ? t('orders.block.people.other', { n: tk.seats.length }) : t('orders.block.people.one')}
                    </span>
                    {/* state label ONLY when the ticket has more than one place
                        (QA 4.5) — a single row would be echoed. A read-only
                        past card suppresses the amber "{n} to assign" nag
                        (the done/waiting reads stay). */}
                    {multi && !(readOnly && needN > 0) && (
                      <span
                        data-testid="orders-block-state"
                        style={{ flex: 'none', fontSize: 10.5, fontWeight: 800, color: needN > 0 ? 'var(--tru-status-amber)' : 'var(--tru-status-done-text)' }}
                      >
                        {needN > 0
                          ? t('orders.block.toAssign', { n: needN })
                          : anyPending ? t('orders.block.waiting') : t('orders.block.complete')}
                      </span>
                    )}
                  </div>

                  {/* place rows */}
                  {tk.seats.map((seat, si) => {
                    const rec = recordFor(ti, si)
                    const filled = seat.status !== 'unassigned'
                    const pending = seat.status === 'pending'
                    const unassignable = seatUnassignable(tk, seat)
                    const seatTicketId = seat.ticketId ?? tk.id
                    const canAssign = !filled && !unassignable && !inert
                    const canEdit = filled && !inert
                    const name = seat.holder?.name
                    const email = seat.holder?.email
                    const label = filled ? (name || email || '') : t('orders.row.unassigned')
                    const sub = !filled
                      ? (inert
                          ? t('orders.row.subEnded')
                          : unassignable
                            ? t('orders.row.subNoSeat')
                            : '')
                      : rec ? recordSub(rec, t, seat.status) : name && email ? email : ''
                    const hasTag = multi
                    // Explicit placement for every cell (QA §3 "why"): the
                    // leading gutter holds either the ✎ button or the
                    // connector, so the text origin can never move with state.
                    // Desktop columns: 1 gutter · 2 dot · 3 seat tag · 4 body · 5 action.
                    // Mobile columns:  1 gutter · 2 dot · 3 everything else, stacked rows.
                    const cell = (col: 1 | 2 | 3, row: number): CSSProperties =>
                      ({ gridColumn: col, gridRow: isMobile ? row : 1 })
                    const bodyRow = hasTag ? 2 : 1
                    const actionRow = hasTag ? 3 : 2
                    return (
                      <div key={`${seatTicketId}.${seat.seatNo}`} data-testid="orders-place-row" data-seat-key={seatKeyFor(data.ref, ti, si)} style={rowStyle}>
                        {/* col 1 — leading gutter: ✎ (filled) or connector (empty) */}
                        {canEdit ? (
                          <button
                            data-testid="orders-edit"
                            title={t('orders.row.editTitle')}
                            onClick={() => onEditSeat?.(seatTicketId, seat.seatNo)}
                            style={{
                              ...cell(1, 1),
                              width: isMobile ? 40 : 36, height: isMobile ? 40 : 36, borderRadius: 8,
                              border: '1px solid var(--tru-border-14)', background: 'rgba(255,255,255,.04)',
                              color: 'var(--tru-text-tertiary)', fontFamily: 'inherit', fontSize: 12, cursor: 'pointer',
                              display: 'grid', placeItems: 'center',
                            }}
                          >✎</button>
                        ) : (
                          <span
                            aria-hidden
                            data-testid="orders-connector"
                            style={{ ...cell(1, 1), justifySelf: 'center', width: 12, height: 2, borderRadius: 2, background: P.c, opacity: 0.75 }}
                          />
                        )}
                        {/* col 2 — status dot (pending = teal @ .45, not a third hue) */}
                        <span
                          data-testid="orders-place-dot"
                          data-state={!filled ? 'need' : pending ? 'pending' : 'done'}
                          aria-hidden
                          style={{
                            ...cell(2, 1),
                            width: 8, height: 8, borderRadius: '50%',
                            background: !filled ? 'var(--tru-status-amber)' : 'var(--tru-status-teal)',
                            ...(pending ? { opacity: 'var(--tru-status-pending-opacity)' } : {}),
                          }}
                        />
                        {/* col 3 — seat tag: EVERY row state on a multi-place
                            ticket; omitted entirely on single-place (QA 4.2/4.3) */}
                        {hasTag && (
                          <span
                            data-testid="orders-seat-tag"
                            className="mono"
                            style={{ ...cell(3, 1), fontSize: 9.5, letterSpacing: '.06em', color: 'var(--tru-text-tertiary)', whiteSpace: 'nowrap' }}
                          >{t('orders.row.seatTag', { n: seat.seatNo })}</span>
                        )}
                        {/* col 4 (desktop) / col 3 row 2 (mobile) — the person */}
                        <div style={{ ...(isMobile ? { gridColumn: 3, gridRow: bodyRow } : { gridColumn: 4, gridRow: 1 }), minWidth: 0 }}>
                          <div
                            data-testid="orders-place-label"
                            style={{ fontSize: 13.5, fontWeight: 700, overflowWrap: 'anywhere', color: filled ? 'var(--tru-text-primary)' : inert || unassignable ? 'var(--tru-text-tertiary)' : 'var(--tru-status-amber)' }}
                          >{label}</div>
                          {sub && (
                            <div data-testid="orders-place-sub" style={{ fontSize: 11, marginBlockStart: 1, color: 'var(--tru-text-quaternary)', overflowWrap: 'anywhere' }}>{sub}</div>
                          )}
                        </div>
                        {/* col 5 (desktop) / col 3 next row (mobile) — action */}
                        {canAssign && (
                          <div style={{ ...(isMobile ? { gridColumn: 3, gridRow: actionRow } : { gridColumn: 5, gridRow: 1 }), display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end' }}>
                            <button
                              data-testid="orders-assign-cta"
                              onClick={() => onAssignSeat?.(seatTicketId, seat.seatNo)}
                              style={{ border: 0, borderRadius: 9, minHeight: 38, padding: '0 14px', fontFamily: 'inherit', fontSize: 12, fontWeight: 800, cursor: 'pointer', whiteSpace: 'nowrap', background: 'var(--tru-status-teal)', color: 'var(--tru-accent-ink)' }}
                            >{t('orders.row.assignNow')}</button>
                          </div>
                        )}
                        {pending && (
                          <span
                            data-testid="orders-invited-tag"
                            className="mono"
                            style={{ ...(isMobile ? { gridColumn: 3, gridRow: actionRow } : { gridColumn: 5, gridRow: 1 }), justifySelf: 'end', fontSize: 9, letterSpacing: '.08em', color: 'var(--tru-text-quaternary)', whiteSpace: 'nowrap' }}
                          >{t('orders.row.invitedTag')}</span>
                        )}
                      </div>
                    )
                  })}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Live card wrapper — allocation-merged seats per order; honest states
// ---------------------------------------------------------------------------

/** Honest in-card notice while allocation state is unreadable: the header
 *  (real box-office data) renders; counts are never invented. Copy is
 *  state-accurate — a signed-out session is NEVER blamed on the build. */
export function AllocNotice({ state, style }: {
  state: Exclude<AllocState, 'ready'>
  style?: CSSProperties
}) {
  const { t } = useLocale()
  const base: CSSProperties = { padding: '12px 16px', fontSize: 11.5, color: 'var(--tru-text-quaternary)', lineHeight: 1.5, ...style }
  if (state === 'signed-out') {
    return (
      <p role="status" data-wired="false" data-alloc-state="signed-out" style={base}>
        {t('orders.signin.prompt')}{' '}
        <button
          data-testid="orders-signin"
          onClick={() => { void startSignIn() }}
          style={{ border: 0, background: 'transparent', padding: 0, fontFamily: 'inherit', fontSize: 11.5, fontWeight: 700, color: 'var(--tru-status-teal-bright)', cursor: 'pointer', textDecoration: 'underline' }}
        >{t('orders.signin.cta')}</button>
      </p>
    )
  }
  const msg =
    state === 'loading'
      ? t('orders.alloc.loading')
      : state === 'unavailable'
        ? t('orders.alloc.unavailable')
        : t('orders.alloc.unconfigured')
  return <p role="status" data-wired="false" data-alloc-state={state} style={base}>{msg}</p>
}

function LiveOrderCard({ order, expanded, onToggle, typeColours, tagCol, onPastKnown }: {
  order: Order
  expanded: boolean
  onToggle: () => void
  typeColours: Map<string, TypeColour>
  tagCol: 56 | 0
  /** PARTITION SEAM (BOARDROOM-2026-09-01-175306 item 2): reports whether
   *  this order's event settled PAST (my-api allocations eventEndsAt + grace
   *  — isPastEvent) so the screen can section it under "Past events". The
   *  card ALSO renders itself read-only on its own past verdict (defence in
   *  depth: no frame ever shows a live CTA on a past event, wherever the card
   *  is mounted). Absent dates ⇒ reports false ⇒ stays Current. */
  onPastKnown?: (orderId: string, past: boolean) => void
}) {
  const { eventById } = useData()
  const { t, dir } = useLocale()
  const navigate = useNavigate()
  const { state, tickets, allocations } = useOrderSeats(order)
  // POST-EVENT CLOSE: eventClosed comes ONLY from the server-computed
  // readiness payload; absent readiness reads open (BOARDROOM-2026-08-25-044751).
  const live = useGetReadyLiveOptional()
  const eventClosed = live?.events.find((e) => e.id === order.eventId)?.eventClosed === true
  // PAST = server-carried ends_at + settle grace behind now (the ONE shared
  // boundary, src/data/pastEvents.ts). Missing/absent dates ⇒ Current.
  const past = isPastEvent(allocations?.eventEndsAt)
  // EVIDENCE GATE (prod incident 2026-09-07, BOARDROOM-070114): report a past
  // verdict ONLY once the allocations wire has actually been read. A freshly
  // (re)mounted card has allocations=null ⇒ past=false — reporting that would
  // un-past an order the moment it remounts inside the expanded Past section,
  // bouncing it Current⇄Past in an infinite remount/refetch loop that pins the
  // card on the loading notice (the OnePulse legacy-order hang). Absent
  // evidence the parent's existing verdict stands; orders still BOOT Current
  // via the pastById default, so the degrade contract is unchanged.
  const pastKnown = allocations !== null
  useEffect(() => {
    if (pastKnown) onPastKnown?.(order.id, past)
  }, [order.id, past, pastKnown, onPastKnown])
  const ev = eventById(order.eventId)
  const data: OrderCardData = {
    ref: order.ref,
    event: ev?.name ?? 'Your event',
    // EVENT-tier date: my-api eventStartsAt (authoritative) via the canonical
    // date token, else the box-office `when` date-part; '' ⇒ nothing renders.
    eventDate: orderEventDate(allocations?.eventStartsAt, ev?.when),
    date: order.purchasedAt,
    total: money(order.total),
    tickets,
    // Invoice slot (item 4, DARK): resolved ONLY through invoiceUrlForOrder —
    // undefined today (endpoint shape pending from tickets), so no link and
    // never a dead one. See src/data/pastEvents.ts.
    invoiceUrl: invoiceUrlForOrder(order),
  }
  if (state !== 'ready') {
    // Header only + honest notice — no invented summary/seat states.
    return (
      <div
        id={`orders.card.${data.ref}`}
        data-testid="orders-card"
        data-order-ref={data.ref}
        style={{ background: 'var(--tru-surface-2)', border: 'var(--tru-slate-border)', borderRadius: 'var(--tru-slate-radius)', overflow: 'hidden', marginBlockEnd: 'var(--tru-card-gap)', boxShadow: 'var(--tru-slate-halo), var(--tru-slate-shadow)' }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '14px 16px', background: 'var(--tru-slate-header-fill)', borderBlockEnd: 'var(--tru-slate-header-rule)' }}>
          <span style={{ flex: 1, minWidth: 0 }}>
            {/* EVENT tier (white) — name + the event's OWN date, same colour +
                size so they read as one event line. */}
            <span style={{ display: 'block', fontSize: 15, fontWeight: 700, color: 'var(--tru-text-primary)', letterSpacing: '-0.01em', lineHeight: 1.25, overflowWrap: 'anywhere' }}>{data.event}</span>
            {data.eventDate && (
              <span data-testid="orders-event-date" style={{ display: 'block', fontSize: 15, fontWeight: 600, color: 'var(--tru-text-primary)', letterSpacing: '-0.01em', lineHeight: 1.25, marginBlockStart: 2, overflowWrap: 'anywhere' }}>{data.eventDate}</span>
            )}
            {/* ORDER tier — ONE accent hue, UNIFORM size (eyebrow · id · date).
                Allocation is unreadable here so needs-action is unknown ⇒ the
                resting (green) accent, never a false amber nag. */}
            <span data-testid="orders-line" className="mono" style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 6, marginBlockStart: 8, fontSize: 10.5, fontWeight: 700, letterSpacing: '.04em', color: 'var(--tru-status-done-text)' }}>
              <span style={{ letterSpacing: '.14em' }}>{t('orders.card.eyebrow')}</span>
              <span data-testid="orders-ref" style={{ letterSpacing: '.05em' }}>{data.ref}</span>
              <span aria-hidden>·</span>
              <span>{data.date}</span>
            </span>
          </span>
          <div style={{ textAlign: 'end', flex: 'none' }}>
            <div data-testid="orders-total" style={{ fontSize: 15, fontWeight: 800, whiteSpace: 'nowrap' }}>{data.total}</div>
            <InvoiceLink href={data.invoiceUrl} chev={dir === 'rtl' ? '‹' : '›'} label={t('orders.card.invoice')} />
          </div>
        </div>
        <AllocNotice state={state} />
      </div>
    )
  }
  const openAssign = (ticketId: string, seatNo: number) =>
    navigate(`/orders/${order.ref}/tickets/${ticketId}/seats/${seatNo}/assign`)
  return (
    <OrderCard
      data={data}
      expanded={expanded}
      onToggle={onToggle}
      eventClosed={eventClosed || past}
      readOnly={past}
      typeColours={typeColours}
      tagCol={tagCol}
      // No assign/edit handlers on a past card — the affordances are already
      // suppressed by readOnly; withholding the handlers is defence in depth.
      onAssignSeat={past ? undefined : openAssign}
      onEditSeat={past ? undefined : openAssign}
    />
  )
}

// ---------------------------------------------------------------------------
// Screen — `orders.root` (routes /orders + /orders/:orderRef)
// ---------------------------------------------------------------------------

export function Orders() {
  const { liveState, orders, ticketsForOrder } = useData()
  const { showPageHead } = useShellChrome()
  const { t } = useLocale()
  const { orderRef } = useParams()
  // REMOVED-HOLDINGS carry (build item #6, BOARDROOM-2026-09-05-121056): off
  // the shared Get-ready live read's /me/holdings envelope — WIRE-ONLY (never
  // a client-side past-holdings signal). EVIDENCE GATE (review fix F5): an
  // empty removedHoldings is only trusted once the context has actually READ
  // the wire (state === 'ready') — while it is loading/unavailable/absent the
  // empty state below renders the NEUTRAL no-claim copy, upgrading to the
  // "yet" line or the removed variant when the evidence settles.
  const getReadyLive = useGetReadyLiveOptional()
  const removedKnown = getReadyLive?.state === 'ready'
  const removedHoldings = removedKnown ? getReadyLive.removedHoldings : []
  // Cards boot expanded; the caret/header collapses a card locally. A
  // deep-linked /orders/:orderRef keeps that card expanded regardless.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({})
  const toggle = (ref: string) => setCollapsed((c) => ({ ...c, [ref]: !c[ref] }))

  // CURRENT vs PAST partition (BOARDROOM-2026-09-01-175306 items 2–3; grace
  // per MY-2026-09-01-180752). The verdict comes off each card's OWN my-api
  // allocations read (eventEndsAt + PAST_SETTLE_GRACE_MS via isPastEvent —
  // src/data/pastEvents.ts holds the ONE boundary), reported up through
  // onPastKnown. Until/unless a date arrives an order sits in Current (the
  // degrade contract: absent fields never wrongly bury an order), and the card
  // itself already renders read-only on its own past verdict, so no frame ever
  // shows a live CTA on a past event while the section settles.
  const [pastById, setPastById] = useState<Record<string, boolean>>({})
  const onPastKnown = useCallback((orderId: string, past: boolean) => {
    setPastById((m) => (!!m[orderId] === past ? m : { ...m, [orderId]: past }))
  }, [])
  const currentOrders = orders.filter((o) => !pastById[o.id])
  const pastOrders = orders.filter((o) => pastById[o.id])
  // Past section boots COLLAPSED (read-only archive, below the fold).
  const [showPast, setShowPast] = useState(false)
  const browseUrl = browseEventsUrl()

  // WHOLE-LIST decisions (QA 3.8 + colour rule): type colours + the seat-tag
  // gutter are computed over EVERY order in the list (current AND past) from
  // the synchronous box-office ticket data, so cards can never disagree with
  // each other — a type keeps its colour when its order settles into Past.
  const listTickets = orders.map((o) => ({ tickets: ticketsForOrder(o.id) }))
  const colours = typeColourMap(listTickets)
  const tagCol = tagColWidth(listTickets)

  const card = (o: Order) => (
    <LiveOrderCard
      key={o.id}
      order={o}
      expanded={orderRef === o.ref ? true : !collapsed[o.ref]}
      onToggle={() => toggle(o.ref)}
      typeColours={colours}
      tagCol={tagCol}
      onPastKnown={onPastKnown}
    />
  )

  return (
    <div id="orders.root" style={{ maxWidth: 'var(--content-max, 700px)' }}>
      {/* Desktop-only page head (mobile hides page h1s) */}
      {showPageHead && (
        <div id="orders.head" data-testid="orders-head">
          <h1 style={{ fontSize: 24, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 4px' }}>{t('orders.head.title')}</h1>
          {/* Verbatim V1.9.1 subtitle (QA 5.6). */}
          <p style={{ fontSize: 13, color: 'var(--tru-text-secondary)', margin: '0 0 16px' }}>{t('orders.head.sub')}</p>
        </div>
      )}
      {liveState !== 'ready' ? (
        <LiveDataNotice liveState={liveState} />
      ) : orders.length === 0 ? (
        removedHoldings.length > 0 ? (
          // REMOVED-HOLDINGS empty state (item #6): this account HAD a ticket
          // (transferred away / reassigned / CS-changed — the wire says so).
          // "…yet" would be a lie and a support-ticket generator — the copy
          // drops it and the quiet "Recent changes" breadcrumb says what
          // happened (actor TYPE only; recipient identity never rendered).
          <div data-testid="orders-empty" className="secondary" style={{ fontSize: 13 }}>
            <p style={{ fontSize: 13, margin: 0 }}>{t('orders.empty.removed')}</p>
            <RemovedHoldingsNote entries={removedHoldings} />
          </div>
        ) : (
          // Honest empty state. `none` (the optimistic "yet" claim) is
          // EVIDENCE-GATED (review fix F5): it renders only once the shared
          // get-ready context has READ the holdings wire and reports no
          // removed holdings — while that context is loading/unavailable (or
          // no provider is mounted) the NEUTRAL line makes no claim either
          // way, and this branch upgrades in place when the context settles.
          <p data-testid="orders-empty" className="secondary" style={{ fontSize: 13 }}>
            {t(removedKnown ? 'orders.empty.none' : 'orders.empty.neutral')}
          </p>
        )
      ) : (
        <>
          {currentOrders.map(card)}
          {/* No CURRENT orders (everything settled into Past) — honest empty
              line + a browse link (item 3). The link is the box-office root
              fallback (see browseEventsUrl — clearly marked; no marketing/
              browse URL pattern exists yet); unconfigured ⇒ honestly absent. */}
          {currentOrders.length === 0 && (
            <p data-testid="orders-empty-current" className="secondary" style={{ fontSize: 13 }}>
              {t('orders.empty.current')}
              {browseUrl && (
                <>
                  {' '}
                  <a
                    data-testid="orders-browse"
                    href={browseUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ fontWeight: 700, color: 'var(--tru-status-teal-bright)', textDecoration: 'none' }}
                  >{t('orders.empty.browse')}</a>
                </>
              )}
            </p>
          )}
          {/* PAST EVENTS — collapsed, strictly read-only archive beneath
              Current (BOARDROOM-2026-09-01-175306 items 2–3). */}
          {pastOrders.length > 0 && (
            <div data-testid="orders-past-section" style={{ marginBlockStart: 20 }}>
              <button
                data-testid="orders-past-toggle"
                aria-expanded={showPast}
                onClick={() => setShowPast((v) => !v)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                  border: 0, background: 'transparent', padding: '8px 0', cursor: 'pointer',
                  fontFamily: 'inherit', textAlign: 'start', color: 'var(--tru-text-secondary)',
                }}
              >
                <span aria-hidden style={{ fontSize: 12, color: 'var(--tru-text-tertiary)' }}>{showPast ? '▾' : '▸'}</span>
                <span style={{ fontSize: 13, fontWeight: 800 }}>{t('orders.past.title')}</span>
                <span className="mono" style={{ fontSize: 10.5, color: 'var(--tru-text-quaternary)' }}>{pastOrders.length}</span>
              </button>
              {showPast && pastOrders.map(card)}
            </div>
          )}
        </>
      )}
    </div>
  )
}
